/* ImageDetailsRenderer.js */

"use strict";

const _ = require("lodash");
const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../../../RegistrationNamespaces");

class ImageDetailsRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("iot-image-details-view-gl");
        this._rootElem.component = this._model;

        this._imagesSection = undefined;
        this._emptyGridContainer = document.createElement("div");
        this._onUserSorted = this._onUserSorted.bind(this);
        this._imageEntityGrid = undefined;
        this._defaultViewActive = false;

        this._logger = this.model.logger;
        this._imagesSectionContainer = document.createElement("div");
        this._imagesSectionContainer.classList.add("image-grid-section");

        // Lines number display container
        this._linesNumberContainer = document.createElement("div");
        this._linesNumberContainer.classList.add("lines-number-container");
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.pagingFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );
    }

    _registerGridListeners() {
        if (this._imageEntityGrid) {
            this._imageEntityGrid.on("UserSorted", this._onUserSorted);
        }
    }

    _unregisterListeners() {
        if (!_.isUndefined(this._model.pagingFeed)) {
            this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
        }
    }

    _unregisterGridListeners() {
        if (this._imageEntityGrid) {
            this._imageEntityGrid.removeListener("UserSorted", this._onUserSorted);
        }
    }

    async _shutdown() {
        this._unregisterListeners();
        this._unregisterGridListeners();

        if (this._defaultViewActive === true) {
            await this._hideDefaultMessageDisplay().catch(() => {});
        }

        let promArr = [_.invoke(this._imageEntityGrid, "shutdown") || Promise.resolve()];

        return Promise.all(promArr).then(
            () => {
                this._imagesSection = undefined;
                this._logger = undefined;
                this._model = undefined;
                this._emptyGridContainer = null;
                this._linesNumberContainer = null;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._defaultViewActive = undefined;
                this._initialEntityCount = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "ImageDetailsRenderer.shutdown() error.");
            }
        );
    }

    _renderImages() {
        this._imagesSectionContainer.innerHTML = "";

        this._imageEntityGrid = new EntityGrid({
            gridKey: this.model._key,
            logger: this._logger,
            singleSelect: true,
            columns: this._model.columnConfig,
            pagingFeed: this._model.pagingFeed,
            showHeader: true,
            showLinesNumber: this._linesNumberContainer,
            modalUserFeedback: this._model._modalUserFeedback,
            userGridData: this._model._userGridData,
        });
        // create entity grid and render it within this._imagesSectionContainer
        this._imagesSectionContainer.appendChild(this._imageEntityGrid.renderer.rootElement);
        this._registerGridListeners();

        this._onFeedRefreshView();
    }

    _showDefaultMessageDisplay() {
        const message = {
            message: "Add an Image",
            icon: "osmanager.add_images",
            action: Registrations.ODM_OS_PLATFORM_EDITOR_ACTIONS_ADD_IMAGES,
        };
        return this._model._gameEditor
            .showDefaultMessageView(message, this._imagesSectionContainer)
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ImageDetailsRenderer._showDefaultMessageDisplay error"
                );
            });
    }

    _hideDefaultMessageDisplay() {
        return this._model._gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ImageDetailsRenderer._hideDefaultMessageDisplay error"
                );
                throw err;
            });
    }

    _isEmpty() {
        return this._model.pagingFeed.totalEntities === 0;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this.model
            .whenModelIsReady()
            .then(() => {
                this._rootElem.appendChild(this._imagesSectionContainer);
                this._rootElem.appendChild(this._linesNumberContainer);

                this._initialEntityCount = this._model.pagingFeed.totalEntities;
                this._renderImages();
                this._registerListeners();
                return;
            })
            .catch((err) => {
                this._logger.warn({error: err}, "ImageDetailsRenderer.render() - Error");
            });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onUserSorted(eventData) {
        this._model._onUserSorted(eventData);
    }

    _onFeedRefreshView() {
        const totalEntities = this._model.pagingFeed.totalEntities;
        if (totalEntities === 0) {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /*******************************************/
}

module.exports = ImageDetailsRenderer;
